import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from './ModalContext';
import SearchSelect from 'components/forms/basefields/SearchSelect';
import Dropdown from 'components/forms/basefields/Dropdown';
import { FieldOption } from 'types/FieldTypes';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { saveData } from 'services/api/saveData';

interface LinkRelatedOrganizationModalProps {
    organizationId: number; // The organization of the page the other organization is linked to
    hasParent: boolean; // Whether the page organization already has a parent organization
    refetchData: () => void;
}

// The relation the selected organization gets to the page organization
type RelationType = 'parent' | 'daughter';

// The selectable relation options of the dropdown
const RELATION_OPTIONS: { value: RelationType; name: string }[] = [
    { value: 'parent', name: 'organization.general.parent_organization_role_label' },
    { value: 'daughter', name: 'organization.general.daughter_organization_role_label' },
];

/*
 * LinkRelatedOrganizationModal.tsx
 * Lets the user search an organization and choose whether it becomes
 * the parent or a daughter of the organization of the page. A parent
 * is saved on the page organization, a daughter gets the page
 * organization as its parent.
 */
const LinkRelatedOrganizationModal: React.FC<LinkRelatedOrganizationModalProps> = ({ organizationId, hasParent, refetchData }) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { revealModal, closeModal } = useModal();
    const [selectedOrganization, setSelectedOrganization] = useState<FieldOption | null>(null);
    const [relation, setRelation] = useState<RelationType | null>(null);
    const [buttonLoader, setButtonLoader] = useState(false);

    // Reveal the modal on first render
    useEffect(() => { revealModal() }, []);

    // Build the search query, only other organizations are offered
    const query = useMemo(() => ({
        endpoint: 'get_contact_search',
        params: { entity_type: 'organization', exclude_id: organizationId },
    }), [organizationId]);

    // With a parent already linked only a daughter can be added
    const relationOptions = useMemo(() => (
        hasParent ? RELATION_OPTIONS.filter(option => option.value === 'daughter') : RELATION_OPTIONS
    ), [hasParent]);

    // Save the relation and close the modal
    const handleSubmit = async () => {
        if (!selectedOrganization || !relation) return;
        setButtonLoader(true);

        // A parent is saved on the page organization, a daughter gets the page organization as parent
        if (relation === 'parent') {
            await saveData({ apiUrl: `patch_organization/${organizationId}`, method: 'patch', data: { parent_organization: selectedOrganization.id } });
        } else {
            await saveData({ apiUrl: `patch_organization/${selectedOrganization.id}`, method: 'patch', data: { parent_organization: organizationId } });
        }

        // Refetch the page data and close the modal
        refetchData();
        setButtonLoader(false);
        closeModal();
        setFloatingAlert({ type: 'success' });
    };

    return (
        <div>
            <div className='formset add-edit-form'>
                <div className='formfield'>
                    <SearchSelect
                        type='searchselect'
                        name='related_organization'
                        placeholder='organization.general.search_organization_label'
                        objectName='organization.general.object_name.singular'
                        query={query}
                        selectionFormat='name'
                        optionFormat={{
                            title: { field: 'name', format: (value) => value },
                            subtitle: { field: 'primary_location', format: (value) => value?.address, optional: true },
                        }}
                        disableAddNew={true}
                        onSelect={(option) => setSelectedOrganization(option)} />
                </div>
                <div className='formfield'>
                    <label>{t('organization.general.relation_label')}</label>
                    <Dropdown<{ value: RelationType; name: string }>
                        name='relation'
                        options={relationOptions}
                        selectedOption={relationOptions.find(option => option.value === relation) ?? null}
                        onChange={(value) => setRelation(value as RelationType)}
                        selectionFormat={(option) => option.name}
                        optionFormat='name'
                        disabled_selected='organization.general.relation_placeholder'
                        allowNoneOption={false}
                        showSearch={false} />
                </div>
                <div className='buttons-right'>
                    <SecondaryButton
                        onClick={() => closeModal()}
                        label='general.cancel'
                        size='small' />
                    <PrimaryButton
                        onClick={handleSubmit}
                        label='general.save'
                        size='small'
                        onlyViewRestriction={true}
                        loading={buttonLoader}
                        disabled={{ disabled: !selectedOrganization || !relation }} />
                </div>
            </div>
        </div>
    );
};

export default LinkRelatedOrganizationModal;
